import { Head, Link, router } from '@inertiajs/react'
import { PageHeader, PageContent } from '@/components/PageHeader'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import type { Category } from '@/types'
import { useState } from 'react'
import { ArrowLeftIcon, PencilIcon, Trash2Icon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  category: Category
}

const kindColors = {
  revenue: 'bg-success/8 text-success border-success/15',
  cost: 'bg-destructive/8 text-destructive border-destructive/15',
  transfer: 'bg-primary/8 text-primary border-primary/15',
}

export default function Show({ category }: Props) {
  const [confirmDelete, setConfirmDelete] = useState(false)

  return (
    <>
      <Head title={category.name} />
      <PageHeader title={category.name} description="Category details">
        <Link href="/categories">
          <Button variant="outline" size="sm" className="gap-1.5">
            <ArrowLeftIcon className="h-3.5 w-3.5" />
            Back
          </Button>
        </Link>
      </PageHeader>

      <PageContent>
        <div className="max-w-lg">
          <Card>
            <CardContent className="p-5">
              <dl className="space-y-5">
                <div>
                  <dt className="text-[13px] font-medium text-muted-foreground">Name</dt>
                  <dd className="mt-1 text-lg font-semibold text-foreground">{category.name}</dd>
                </div>
                <div>
                  <dt className="text-[13px] font-medium text-muted-foreground">Type</dt>
                  <dd className="mt-1.5">
                    <Badge
                      variant="outline"
                      className={cn('capitalize text-[12px] px-2.5 py-0.5', kindColors[category.kind])}
                    >
                      {category.kind}
                    </Badge>
                  </dd>
                </div>
              </dl>

              <div className="flex gap-2 mt-6 pt-5 border-t border-border">
                <Link href={`/categories/${category.id}/edit`}>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <PencilIcon className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/8"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2Icon className="h-3.5 w-3.5" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContent>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete category?"
        description="This action cannot be undone. Transactions using this category will be left uncategorized."
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => router.delete(`/categories/${category.id}`)}
      />
    </>
  )
}
